import type { TTwoFactorSetupResponse, TTwoFactorStatusResponse } from "@/api/twoFactor";
import { useCallback, useEffect, useMemo, useState } from "react";

import { newPasswordSchema } from "@/lib/validation/auth";
import { twoFactorService } from "@/api/twoFactor";
import { useStore } from "@/store";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import { Button } from "../Button";
import { Input } from "../Input/Input";
import { useToast } from "../Toast";

const passwordSchema = z
  .object({
    newPassword: newPasswordSchema,
    confirmPassword: z.string().min(1, "Please confirm your password"),
  })
  .refine((values) => values.newPassword === values.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type TPasswordFormValues = z.infer<typeof passwordSchema>;

export const SecuritySettings = () => {
  const { toast } = useToast();
  const updateProfile = useStore.use.updateProfile();
  const fetchProfile = useStore.use.fetchProfile();

  // Password change
  const passwordForm = useForm<TPasswordFormValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { newPassword: "", confirmPassword: "" },
  });

  const handlePasswordUpdate = useCallback(async () => {
    await passwordForm.handleSubmit(async (values) => {
      const result = await updateProfile({ password: values.newPassword });
      if (result.success) {
        toast({ variant: "success", title: "Password updated" });
        passwordForm.reset();
        await fetchProfile({ force: true });
        return;
      }

      toast({ variant: "destructive", title: result.error ?? "Failed to update password" });
    })();
  }, [fetchProfile, passwordForm, toast, updateProfile]);

  // 2FA
  const [status, setStatus] = useState<TTwoFactorStatusResponse | null>(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const [setup, setSetup] = useState<TTwoFactorSetupResponse | null>(null);
  const [isSettingUp, setIsSettingUp] = useState(false);
  const [enableToken, setEnableToken] = useState("");
  const [isEnabling, setIsEnabling] = useState(false);
  const [disablePassword, setDisablePassword] = useState("");
  const [isDisabling, setIsDisabling] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);

  const enabled = Boolean(status?.enabled);

  const loadStatus = useCallback(async () => {
    setIsLoadingStatus(true);
    const result = await twoFactorService.getTwoFactorStatus();
    setIsLoadingStatus(false);
    if (result.error) {
      toast({ variant: "destructive", title: "Failed to load 2FA status" });
      return;
    }
    setStatus(result.data ?? null);
  }, [toast]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  const handleSetup = useCallback(async () => {
    setIsSettingUp(true);
    const result = await twoFactorService.setupTwoFactor();
    setIsSettingUp(false);
    if (result.error) {
      toast({ variant: "destructive", title: "Failed to start 2FA setup" });
      return;
    }
    setSetup(result.data ?? null);
    setBackupCodes(result.data?.backup_codes ?? null);
    toast({ variant: "success", title: "2FA setup started" });
  }, [toast]);

  const handleEnable = useCallback(async () => {
    const token = enableToken.trim();
    if (token.length !== 6) {
      toast({ variant: "destructive", title: "Enter the 6-digit code from your authenticator app" });
      return;
    }

    setIsEnabling(true);
    const result = await twoFactorService.enableTwoFactor({ token });
    setIsEnabling(false);
    if (result.error) {
      toast({ variant: "destructive", title: "Failed to enable 2FA" });
      return;
    }

    toast({ variant: "success", title: result.data?.message ?? "Two-factor enabled" });
    setEnableToken("");
    setSetup(null);
    setBackupCodes(null);
    await fetchProfile({ force: true });
    await loadStatus();
  }, [enableToken, fetchProfile, loadStatus, toast]);

  const handleDisable = useCallback(async () => {
    const password = disablePassword.trim();
    if (!password) {
      toast({ variant: "destructive", title: "Password is required to disable 2FA" });
      return;
    }

    setIsDisabling(true);
    const result = await twoFactorService.disableTwoFactor({ password });
    setIsDisabling(false);
    if (result.error) {
      toast({ variant: "destructive", title: "Failed to disable 2FA" });
      return;
    }

    toast({ variant: "success", title: result.data?.message ?? "Two-factor disabled" });
    setDisablePassword("");
    setSetup(null);
    setBackupCodes(null);
    await fetchProfile({ force: true });
    await loadStatus();
  }, [disablePassword, fetchProfile, loadStatus, toast]);

  const handleRegenerateBackupCodes = useCallback(async () => {
    setIsRegenerating(true);
    const result = await twoFactorService.regenerateBackupCodes();
    setIsRegenerating(false);
    if (result.error) {
      toast({ variant: "destructive", title: "Failed to regenerate backup codes" });
      return;
    }
    setBackupCodes(result.data?.backup_codes ?? null);
    toast({ variant: "success", title: "Backup codes regenerated" });
    await loadStatus();
  }, [loadStatus, toast]);

  const copy = useCallback(
    async (value: string, label: string) => {
      try {
        await navigator.clipboard.writeText(value);
        toast({ variant: "success", title: `${label} copied` });
      } catch (err) {
        console.error("[settings-security] Failed to copy value", { error: err, label });
        toast({ variant: "destructive", title: `Failed to copy ${label}` });
      }
    },
    [toast]
  );

  const statusSummary = useMemo(() => {
    if (!status) return "Unknown";
    if (!status.enabled) return "Disabled";
    return `Enabled (${status.method ?? "totp"})`;
  }, [status]);

  return (
    <div className="space-y-10">
      <div>
        <h3 className="text-lg font-semibold">Security</h3>
        <p className="text-sm text-muted-foreground">Manage your password and two-factor authentication.</p>
      </div>

      {/* Password */}
      <section className="space-y-4">
        <div>
          <div className="text-sm font-semibold">Change password</div>
          <div className="text-sm text-muted-foreground">Set a new password for your account.</div>
        </div>

        <div className="grid max-w-md gap-3">
          <div>
            <label className="text-sm font-medium leading-none">New password</label>
            <Input
              type="password"
              value={passwordForm.watch("newPassword")}
              onChange={(e) => passwordForm.setValue("newPassword", e.target.value, { shouldValidate: true })}
              autoComplete="new-password"
              className="mt-2 rounded-md border border-input"
            />
            {passwordForm.formState.errors.newPassword?.message ? (
              <div className="mt-1 text-xs text-destructive">{passwordForm.formState.errors.newPassword.message}</div>
            ) : null}
          </div>

          <div>
            <label className="text-sm font-medium leading-none">Confirm password</label>
            <Input
              type="password"
              value={passwordForm.watch("confirmPassword")}
              onChange={(e) => passwordForm.setValue("confirmPassword", e.target.value, { shouldValidate: true })}
              autoComplete="new-password"
              className="mt-2 rounded-md border border-input"
            />
            {passwordForm.formState.errors.confirmPassword?.message ? (
              <div className="mt-1 text-xs text-destructive">
                {passwordForm.formState.errors.confirmPassword.message}
              </div>
            ) : null}
          </div>

          <div className="flex gap-2 pt-1">
            <Button
              type="button"
              onClick={() => void handlePasswordUpdate()}
              disabled={passwordForm.formState.isSubmitting}
            >
              {passwordForm.formState.isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating…
                </>
              ) : (
                "Update password"
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => passwordForm.reset()}
              disabled={passwordForm.formState.isSubmitting}
            >
              Cancel
            </Button>
          </div>
        </div>
      </section>

      {/* 2FA */}
      <section className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-sm font-semibold">Two-factor authentication (2FA)</div>
            <div className="text-sm text-muted-foreground">
              Add an extra layer of security with an authenticator app.
            </div>
            <div className="mt-1 text-xs text-muted-foreground">Status: {statusSummary}</div>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => void loadStatus()}
            disabled={isLoadingStatus}
          >
            {isLoadingStatus ? "Loading..." : "Refresh"}
          </Button>
        </div>

        {!enabled ? (
          <div className="space-y-4 rounded-lg border bg-background p-4">
            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                onClick={() => void handleSetup()}
                disabled={isSettingUp}
              >
                {isSettingUp ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Setting up…</>
                ) : "Set up 2FA"}
              </Button>
              {setup ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void copy(setup.secret, "Secret")}
                >
                  Copy secret
                </Button>
              ) : null}
            </div>

            {setup ? (
              <div className="grid gap-4 lg:grid-cols-2">
                <div className="space-y-2">
                  <div className="text-sm font-semibold">Scan QR code</div>
                  <div className="text-sm text-muted-foreground">
                    Scan with Google Authenticator, 1Password, Authy, etc.
                  </div>
                  <div className="inline-block rounded-md border bg-card p-3">
                    <img
                      src={setup.qr_code_url}
                      alt="2FA QR code"
                      className="h-44 w-44"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <div className="text-sm font-semibold">Enable 2FA</div>
                    <div className="text-sm text-muted-foreground">
                      Enter the 6-digit code from your authenticator app.
                    </div>
                  </div>
                  <Input
                    inputMode="numeric"
                    placeholder="123456"
                    value={enableToken}
                    onChange={(e) => setEnableToken(e.target.value)}
                    className="rounded-md border border-input"
                  />
                  <Button
                    type="button"
                    onClick={() => void handleEnable()}
                    disabled={isEnabling || enableToken.trim().length !== 6}
                  >
                    {isEnabling ? "Enabling..." : "Enable 2FA"}
                  </Button>
                </div>
              </div>
            ) : null}

            {backupCodes && backupCodes.length > 0 ? (
              <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold">Backup codes</div>
                    <div className="text-sm text-muted-foreground">
                      Save these in a secure place. Each code can be used once.
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => void copy(backupCodes.join("\n"), "Backup codes")}
                  >
                    Copy codes
                  </Button>
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  {backupCodes.map((code) => (
                    <div
                      key={code}
                      className="rounded-md border bg-muted/30 px-3 py-2 font-mono text-sm"
                    >
                      {code}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="space-y-4 rounded-lg border bg-background p-4">
            <div className="grid gap-2 text-sm text-muted-foreground">
              <div>
                Method: <span className="text-foreground">{status?.method ?? "totp"}</span>
              </div>
              <div>
                Last used: <span className="text-foreground">{status?.last_used_at ?? "—"}</span>
              </div>
              <div>
                Backup codes remaining: <span className="text-foreground">{status?.backup_codes_remaining ?? "—"}</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => void handleRegenerateBackupCodes()}
                disabled={isRegenerating}
              >
                {isRegenerating ? "Regenerating..." : "Regenerate backup codes"}
              </Button>
            </div>

            {backupCodes && backupCodes.length > 0 ? (
              <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold">New backup codes</div>
                    <div className="text-sm text-muted-foreground">These replace your old codes. Save them now.</div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => void copy(backupCodes.join("\n"), "Backup codes")}
                  >
                    Copy codes
                  </Button>
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  {backupCodes.map((code) => (
                    <div
                      key={code}
                      className="rounded-md border bg-muted/30 px-3 py-2 font-mono text-sm"
                    >
                      {code}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="space-y-2 border-t pt-4">
              <div className="text-sm font-semibold">Disable 2FA</div>
              <div className="text-sm text-muted-foreground">
                Enter your password to disable two-factor authentication.
              </div>
              <div className="grid max-w-md gap-2">
                <Input
                  type="password"
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  autoComplete="current-password"
                  className="rounded-md border border-input"
                />
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => void handleDisable()}
                  disabled={isDisabling}
                >
                  {isDisabling ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Disabling…</>
                  ) : "Disable 2FA"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};
